import React, { useState } from 'react';
import { graphql } from 'react-relay';

import {
  getCheckoutParamForCheckoutState,
  getUserHasSelectedFinalQuote,
  navigateToCheckoutRoute,
  redirectToCheckout,
} from '@zego/flows/onlineCheckout/redirects';
import useLoginForm from '@zego/flows/quickEstimates/useLoginForm';
import { getBaseUrlFromProductCategory } from '@zego/flows/redirects';
import useNavigation from '@zego/hooks/useNavigation';
import BasicLayout from '@zego/layouts/BasicLayout';
import withData from '@zego/lib/withData';
import useOnlineCheckoutProgress from '@zego/pageContainers/gb/onlineCheckout/common/useOnlineCheckoutProgress';
import { BREAKPOINTS, getColWidthCalc, unit } from '@zego/theme';
import { useTracking } from '@zego/tracking';

import H2 from '@zego/components/atoms/H2';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import ContentContainer from '@zego/components/ContentContainer';
import Link from '@zego/components/Link';
import LoginForm from '@zego/components/LoginForm';

function getProductNameFromCategory(category) {
  switch (category) {
    case 'GB.VAN':
      return 'Courier Van';
    case 'GB.PRIVATE_HIRE':
    default:
      return 'Private Hire';
  }
}

const NoCheckoutAvailable = ({ productName }) => {
  return (
    <BasicLayout title="Retrieve your quote">
      <ContentContainer>
        <View
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <H2>We couldn’t retrieve your quote.</H2>
          <Spacer height={unit(2)} />
          <View css={{ maxWidth: unit(70) }}>
            <RegularText css={{ marginBottom: unit(1) }}>
              Unfortunately, we weren’t able to retrieve your {productName}{' '}
              insurance quote online. This could be because:
            </RegularText>
            <RegularText css={{ marginLeft: unit(2), marginBottom: unit(1) }}>
              - We cannot find a quote for {productName} insurance associated
              with this email address.
            </RegularText>
            <RegularText css={{ marginLeft: unit(2), marginBottom: unit(1) }}>
              - You’ve had an active policy with Zego
            </RegularText>
            <RegularText>
              To continue with your quote, please call us on 0203 053 9815.
            </RegularText>
          </View>
        </View>
      </ContentContainer>
    </BasicLayout>
  );
};

const LandingPage = props => {
  const [isCheckoutAvailable, setIsCheckoutAvailable] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const getOnlineCheckoutProgress = useOnlineCheckoutProgress();
  const hasNoOnlineCheckout =
    props.viewer.currentUser && !props.viewer.onlineCheckoutProgress;
  const navigation = useNavigation();
  const { trackEvent } = useTracking();
  const categoryCode = navigation.query?.product || 'GB.PRIVATE_HIRE';
  const productName = getProductNameFromCategory(categoryCode);

  const initialValues = {
    email: '',
    password: '',
  };

  const handleSubmit = useLoginForm(
    values => values,
    'Website: Checkout - Authentication - Login form submitted',
  );

  if (!isSubmitting && (hasNoOnlineCheckout || !isCheckoutAvailable)) {
    // If there's a user but no checkout then they may be a scooter/van user,
    // or they are an active user
    return <NoCheckoutAvailable productName={productName} />;
  }

  return (
    <BasicLayout title="Retrieve your quote">
      <ContentContainer>
        <View
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <H2>Retrieve your quote</H2>
          <Spacer height={unit(2)} />
          <RegularText
            css={{
              maxWidth: unit(70),
            }}
          >
            Use your email and password to retrieve your quote.
          </RegularText>
          <RegularText
            css={{
              maxWidth: unit(70),
            }}
          >
            If you refined a quote with us before you should have an email and
            password. If you don't, please{' '}
            <Link href={getBaseUrlFromProductCategory(categoryCode)}>
              get a quote
            </Link>
            .
          </RegularText>
        </View>

        <Spacer height={unit(4)} />
        <View
          css={{
            margin: 'auto',
            opacity: isSubmitting ? 0.5 : 1,
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: isSubmitting ? 'none' : 'auto',
            [BREAKPOINTS.medium]: {
              width: getColWidthCalc(12, 20, 6),
            },
          }}
        >
          <LoginForm
            onSubmit={handleSubmit}
            initialValues={initialValues}
            onSubmitChanged={setIsSubmitting}
            onSuccess={async () => {
              const onlineCheckoutProgress = await getOnlineCheckoutProgress(
                categoryCode,
              );

              if (!onlineCheckoutProgress) {
                trackEvent(
                  `${productName} - Retrieve your Quote Login failure`,
                  {
                    estimateType: categoryCode,
                  },
                );
                setIsCheckoutAvailable(false);
                return;
              }

              trackEvent(
                `${productName} - Retrieve your Quote Login success`,
                {
                  estimateType: categoryCode,
                },
              );

              navigateToCheckoutRoute(
                navigation.push,
                onlineCheckoutProgress.categoryCode,
                getCheckoutParamForCheckoutState(
                  onlineCheckoutProgress.currentCheck,
                ),
              );
            }}
            passwordResetClickEvent="Website: Retrieve Your Quote - Authentication - Password Reset Clicked"
          />
        </View>
      </ContentContainer>
    </BasicLayout>
  );
};

export default withData(LandingPage, {
  query: graphql`
    query checkoutPageQuery($category: String!) {
      viewer {
        currentUser {
          customerNumber # Just for checking it exists
        }
        onlineCheckoutProgress(category: $category) {
          categoryCode
          status
          currentCheck
          isStatusCompleted(status: QUOTE_REFINED)
          selectedProduct {
            code
          }
          onlineCheckoutEnabled
        }
      }
    }
  `,
  getVariables: ({ query }) => {
    return {
      category: query.product || 'GB.PRIVATE_HIRE',
    };
  },
  getHeaders: ({ viewer }, context) => {
    if (
      viewer?.onlineCheckoutProgress &&
      getUserHasSelectedFinalQuote(viewer?.onlineCheckoutProgress?.status)
    ) {
      return redirectToCheckout(viewer, context);
    }
  },
});
